/**
 * Hooks TanStack Query pour la queue de publication (S5b).
 *
 * Convention `queueKeys.*` alignée sur `draftsKeys` — un seul namespace
 * par domaine, les mutations invalident la racine pour rafraîchir tout
 * (badge sidebar futur + listing).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { queueApi, type QueueListFilters } from '@/utils/api/queue'

export const queueKeys = {
  all: ['queue'] as const,
  list: (filters: QueueListFilters) => [...queueKeys.all, 'list', filters] as const,
  detail: (id: number) => [...queueKeys.all, 'detail', id] as const,
}

export function useQueueList(filters: QueueListFilters = {}) {
  return useQuery({
    queryKey: queueKeys.list(filters),
    queryFn: () => queueApi.list(filters),
  })
}

/**
 * Compteur des jobs échoués — alimente le badge "alertes" de la
 * sidebar (équivalent FE du Notifier externe SMTP/Discord côté BE).
 * Poll 60s, pause arrière-plan, refetch on focus.
 */
export function useQueueFailedCount() {
  return useQuery({
    queryKey: [...queueKeys.all, 'failed-count'] as const,
    queryFn: () => queueApi.list({ status: 'failed', limit: 1 }),
    refetchInterval: (query) => (query.state.error ? false : 60_000),
    refetchOnWindowFocus: true,
  })
}

export function useQueueItemDetail(id: number | null) {
  return useQuery({
    queryKey: id !== null ? queueKeys.detail(id) : ['queue', 'detail', 'idle'],
    queryFn: () => queueApi.detail(id!),
    enabled: id !== null,
  })
}

export function useEnqueue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: queueApi.enqueue,
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function usePauseQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => queueApi.pause(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function useResumeQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => queueApi.resume(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function useForcePublishNow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => queueApi.forcePublishNow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function useReorderQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, priority }: { id: number; priority: number }) =>
      queueApi.reorder(id, { priority }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function useDeleteQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => queueApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}
